import { useCallback, useEffect, useMemo, useState } from 'react';

interface Identifiable {
  id: string;
}

export interface RowSelection<T extends Identifiable> {
  selectedIds: Set<string>;
  selectedRows: T[];
  count: number;
  isSelected: (id: string) => boolean;
  toggleRow: (row: T) => void;
  togglePage: () => void;
  clear: () => void;
  allPageSelected: boolean;
  somePageSelected: boolean;
  selectedOffPage: number;
}

/**
 * Row selection for a paginated table.
 *
 * Stores the selected *rows*, not just their ids, so a selection survives
 * paging and can be acted on (exported, bulk-updated) without re-fetching rows
 * the user has navigated away from. Rows still on screen are kept in sync as the
 * query refetches, so an export always reflects current data.
 *
 * @param pageRows the rows currently rendered — used for select-all and syncing
 */
export function useRowSelection<T extends Identifiable>(pageRows: T[]): RowSelection<T> {
  const [selected, setSelected] = useState<Map<string, T>>(new Map());

  // Refresh stored copies of rows that are on screen, so bulk actions and CSV
  // exports never operate on a stale snapshot.
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;

      let changed = false;
      const next = new Map(prev);

      for (const row of pageRows) {
        if (next.has(row.id) && next.get(row.id) !== row) {
          next.set(row.id, row);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [pageRows]);

  const toggleRow = useCallback((row: T) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.set(row.id, row);
      }
      return next;
    });
  }, []);

  /** Selects every row on the current page, or clears them if all are selected. */
  const togglePage = useCallback(() => {
    setSelected((prev) => {
      const next = new Map(prev);
      const allOnPageSelected = pageRows.length > 0 && pageRows.every((row) => next.has(row.id));

      if (allOnPageSelected) {
        pageRows.forEach((row) => next.delete(row.id));
      } else {
        pageRows.forEach((row) => next.set(row.id, row));
      }

      return next;
    });
  }, [pageRows]);

  const clear = useCallback(() => {
    setSelected((prev) => (prev.size === 0 ? prev : new Map()));
  }, []);

  const selectedIds = useMemo(() => new Set(selected.keys()), [selected]);
  const selectedRows = useMemo(() => Array.from(selected.values()), [selected]);

  const pageState = useMemo(() => {
    const onPage = pageRows.filter((row) => selected.has(row.id)).length;
    return {
      /** Every row on this page is selected. */
      allPageSelected: pageRows.length > 0 && onPage === pageRows.length,
      /** Some — but not all — rows on this page are selected. */
      somePageSelected: onPage > 0 && onPage < pageRows.length,
      /** Selected rows that live on a page the user isn't looking at. */
      selectedOffPage: selected.size - onPage,
    };
  }, [pageRows, selected]);

  return {
    selectedIds,
    selectedRows,
    count: selected.size,
    isSelected: useCallback((id: string) => selected.has(id), [selected]),
    toggleRow,
    togglePage,
    clear,
    ...pageState,
  };
}
